import { Eye, Brain, Hand as HandIcon, Type, Lightbulb, ListChecks } from "lucide-react";
import { useExplorer } from "@/context/ExplorerContext";
import { ImageSlot } from "@/components/lurio/ImageSlot";
import { Eyebrow, Section, Tape } from "@/components/lurio/Primitives";
import { Reveal } from "@/components/lurio/Motion";

const ICONS = [Eye, Brain, HandIcon, Type, Lightbulb, ListChecks];

export default function Credibility() {
  const { t } = useExplorer();
  const c = t.credibility;
  return (
    <Section id="pedagogie" bg="bg-sage-soft">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="max-w-2xl lg:col-span-8">
          <Reveal><Eyebrow className="mb-5">{c.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><h2 className="font-serif text-4xl font-bold leading-[1.3] sm:leading-[1.3] lg:leading-[1.3] text-ink sm:text-5xl">{c.title}</h2></Reveal>
        </div>
        <Reveal delay={0.1} className="hidden lg:col-span-4 lg:block">
          <div className="mx-auto max-w-[220px] rotate-[3deg] rounded-book bg-cream-card p-2 shadow-[0_18px_40px_-24px_rgba(30,42,56,0.45)]">
            <ImageSlot id="page_bravo" rounded="rounded-[12px]" />
          </div>
        </Reveal>
      </div>

      <ul data-testid="skills-grid" className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        {c.skills.map((s, i) => {
          const Icon = ICONS[i];
          return (
            <Reveal as="li" key={s.name} delay={i * 0.05} className="paper-card lift p-5 sm:p-6">
              <Icon size={24} strokeWidth={1.7} className="text-blue" aria-hidden />
              <h3 className="mt-4 font-serif text-xl font-bold text-ink">{s.name}</h3>
              <p className="mt-1 text-[0.95rem] leading-[1.75] text-ink-soft">{s.text}</p>
            </Reveal>
          );
        })}
      </ul>

      <div className="mt-16 grid gap-8 lg:grid-cols-12">
        <Reveal className="rounded-book bg-blue p-7 text-cream sm:p-9 lg:col-span-7">
          <p className="eyebrow !text-gold-soft">{c.whyTitle}</p>
          <ol className="mt-6 space-y-6">
            {c.why.map((w, i) => (
              <li key={w.title} className="grid grid-cols-[auto_1fr] gap-4">
                <span className="font-latin text-4xl font-light leading-none text-gold" dir="ltr">{i + 1}</span>
                <div>
                  <h3 className="font-serif text-2xl font-bold">{w.title}</h3>
                  <p className="mt-1 text-[1rem] leading-[1.8] text-cream/80">{w.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </Reveal>
        <Reveal delay={0.1} className="paper-card grain relative rotate-[-1deg] p-7 sm:p-9 lg:col-span-5">
          <Tape className="-top-3 end-8 rotate-[-5deg]" />
          <p className="eyebrow">{c.parentsTitle}</p>
          <ul className="mt-6 space-y-5">
            {c.parents.map((p) => (
              <li key={p.time} className="flex gap-4">
                <span className="font-hand shrink-0 text-2xl leading-tight text-coral">{p.time}</span>
                <p className="text-[1rem] leading-[1.8] text-ink-soft">{p.text}</p>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </Section>
  );
}
